import { countSmsCharacters, normalizeSmsText, SMS_MAX_CHARS, } from '../helpers/gsm7.js';
import { SMS_VARIABLE_PATTERN } from './sms-variables.js';
/**
 * Rendu d'un SMS : fonction **pure**, le cœur testable du canal.
 *
 * Elle vit dans les contrats et non dans le serveur parce que l'éditeur du
 * back-office en dépend autant que l'envoi : le compteur « 155/160 · 1 SMS »
 * affiché sous le textarea doit être calculé par le code qui enverra le message,
 * pas par une seconde implémentation qui dérivera au premier changement de
 * format.
 */
/**
 * Comportement de Brevo sur la mention légale, **constaté par envoi de test**
 * en `type: "marketing"` : le contenu envoyé était « ceci est un test », le
 * téléphone a reçu « ceci est un test STOP au 36199 ».
 *
 * Deux enseignements, tous deux figés ici :
 *
 * 1. Brevo ajoute la mention lui-même, sans qu'on ait à poser le jeton
 *    `[STOP CODE]` que la doc de l'API décrit. Le mode `token-in-body` reste
 *    câblé pour le jour où l'un des deux chemins changerait, mais il n'est pas
 *    celui qu'on emprunte.
 * 2. Elle est collée **à la suite du texte**, précédée d'un simple espace — pas
 *    d'un saut de ligne. « …avant le 31/12. STOP au 36199 » se lit comme une
 *    phrase du message, ce qu'elle n'est pas. On termine donc nous-mêmes le
 *    contenu par un saut de ligne pour que la mention tombe sur sa propre ligne.
 */
export const STOP_SUFFIX_MODE = 'brevo-appends';
/**
 * Saut de ligne ajouté en fin de contenu marketing, pour isoler la mention que
 * Brevo colle derrière. Sans lui, la mention termine la dernière phrase du SMS.
 */
const STOP_LINE_BREAK = '\n';
/**
 * Ce que Brevo ajoute réellement, espace de tête compris : ` STOP au 36199`,
 * relevé sur l'envoi de test. Le code fait 5 chiffres.
 */
const STOP_SUFFIX_APPENDED_BY_BREVO = ' STOP au 36199';
/**
 * Caractères que le compteur réserve : notre saut de ligne + ce que Brevo
 * ajoute. 15 au total, et non 14 — le saut de ligne est facturé comme le reste.
 *
 * C'est ce qui empêche de valider un modèle à 160 qui partirait à 175.
 */
export const STOP_SUFFIX_RESERVED_CHARS = STOP_LINE_BREAK.length + STOP_SUFFIX_APPENDED_BY_BREVO.length;
/** Jeton attendu par l'API Brevo en mode `token-in-body`. */
const STOP_TOKEN = '[STOP CODE]';
/**
 * L'aperçu montre le message tel qu'il arrivera sur le téléphone : notre saut
 * de ligne, puis la mention de Brevo avec son espace de tête. Sa longueur vaut
 * donc exactement {@link STOP_SUFFIX_RESERVED_CHARS}.
 */
const STOP_SUFFIX_PLACEHOLDER = `${STOP_LINE_BREAK}${STOP_SUFFIX_APPENDED_BY_BREVO}`;
/**
 * Substitue les variables, normalise, puis mesure — dans cet ordre.
 *
 * Une variable absente des `params` est **laissée en place** plutôt que
 * remplacée par du vide : un « votre réservation  » amputé partirait sans que
 * personne ne le voie, alors qu'un `{{ bookingReference }}` visible dans
 * l'aperçu se remarque. Elle est aussi remontée dans `missingKeys`, et l'envoi
 * est refusé côté service.
 */
export function renderSms(input) {
    const params = input.params ?? {};
    const missingKeys = [];
    const substituted = input.body.replaceAll(SMS_VARIABLE_PATTERN, (match, name) => {
        const value = params[name];
        if (value === undefined || value === null || value === '') {
            if (!missingKeys.includes(name))
                missingKeys.push(name);
            return match;
        }
        return value;
    });
    const normalized = normalizeSmsText(substituted);
    const isMarketing = input.messageType === 'MARKETING';
    const content = isMarketing ? `${normalized}${marketingSuffix()}` : normalized;
    const preview = isMarketing ? `${normalized}${STOP_SUFFIX_PLACEHOLDER}` : normalized;
    const { charCount, encoding, nonGsmChars, segments } = countSmsCharacters(preview);
    return {
        charCount,
        content,
        encoding,
        exceedsLimit: charCount > SMS_MAX_CHARS,
        missingKeys,
        nonGsmChars,
        preview,
        segments,
    };
}
/**
 * Ce qu'on ajoute au contenu d'un SMS marketing avant de l'envoyer.
 *
 * En `brevo-appends` (le mode constaté), un simple saut de ligne : Brevo écrit
 * la mention derrière. En `token-in-body`, le jeton, que Brevo substitue.
 */
function marketingSuffix() {
    return STOP_SUFFIX_MODE === 'token-in-body' ? `${STOP_LINE_BREAK}${STOP_TOKEN}` : STOP_LINE_BREAK;
}
